import React, { useEffect, Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { DeleteIpdEntry, GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { DeletePaymentReceive, GetPaymentReceives } from "../../../../api/ipd/paymentReceiveAPI";
import { DeleteRoomChange, GetRoomChanges } from "../../../../api/ipd/roomChangeAPI";

const ListRoomChangeEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchPaymentReceives = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetRoomChanges(); // Your API should return a list of OPD entries
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          docType: item.docType || "",
          docNo: item.docNo || "",
          roomNo: item?.roomId?.roomNo || "",
          patientName:
            (item.ipdEntryId?.patientId?.patientNamePrefix ? item.ipdEntryId?.patientId?.patientNamePrefix + " " : "") +
            (item.ipdEntryId?.patientId?.patientName || ""),
          sex: item.ipdEntryId?.patientId?.sex || "",
          age: item.ipdEntryId?.patientId?.age ? `${item.ipdEntryId?.patientId?.age} ${item.ipdEntryId?.patientId?.ageSuffix || ""}` : "",
          mobileNo: item.ipdEntryId?.patientId?.mobileNo || "",
          address: item.ipdEntryId?.patientId?.address || "",
          newRoomNo: item.newRoomId?.roomNo || "",
          newDate: item.newDate || "",
          newTime: item.newTime || "",
        }));
        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchPaymentReceives();
  }, []);

  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  const handleEditClick = id => {
    navigate(`/ipd/transaction/room-change-entry/edit/${id}`);
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteRoomChange(id);
          if (response.success) {
            showToast("success", response.message);
            const newData = data.filter(item => item.id !== id);
            setData(newData);
            setFilteredData(newData);
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  const columns = [
    { name: "S.No", selector: row => row.index, sortable: true, width: "10%" },
    { name: "Doc Type", selector: row => row.docType, sortable: true, width: "10%" },
    { name: "Doc No", selector: row => row.docNo, sortable: true, width: "10%" },
    { name: "Room No", selector: row => row.roomNo, sortable: true, width: "10%" },
    { name: "Patient Name", selector: row => row.patientName, sortable: true, width: "10%" },
    { name: "Sex", selector: row => row.sex, sortable: true, width: "10%" },
    { name: "Age", selector: row => row.age, sortable: true, width: "10%" },
    { name: "Mobile No", selector: row => row.mobileNo, sortable: true, width: "10%" },
    { name: "Address", selector: row => row.address, sortable: true, width: "10%" },
    { name: "New Room No", selector: row => row.newRoomNo, sortable: true, width: "10%" },
    { name: "New Date", selector: row => row.newDate, sortable: true, width: "10%" },
    { name: "New Time", selector: row => row.newTime, sortable: true, width: "10%" },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "20%"
    }
  ];

  const headers = [
    { label: "S.No", key: "index" },
    { label: "Doc Type", key: "docType" },
    { label: "Doc No", key: "docNo" },
    { label: "Room No", key: "roomNo" },
    { label: "Patient Name", key: "patientName" },
    { label: "Sex", key: "sex" },
    { label: "Age", key: "age" },
    { label: "Mobile No", key: "mobileNo" },
    { label: "Address", key: "address" },
    { label: "New Room No", key: "newRoomNo" },
    { label: "New Date", key: "newDate" },
    { label: "New Time", key: "newTime" }
  ];

  const ExportCSV = ({ data }) => (
    <CSVLink data={data} headers={headers} filename="room-change-entries.csv">
      <button className="btn btn-primary mt-1" id="export">
        Export
      </button>
      <UncontrolledTooltip placement="top" target="export">
        Export to CSV
      </UncontrolledTooltip>
    </CSVLink>
  );

  return (
    <Fragment>
      <Breadcrumb parent="Room Change Entries" title="List Room Change Entries" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5>List Room Change Entries</h5>
                  <Link to={`/ipd/transaction/room-change-entry/add`}>
                    <button className="btn btn-primary" id="create">
                      Create New
                    </button>
                    <UncontrolledTooltip placement="top" target="create">
                      Create New Room Change Entry
                    </UncontrolledTooltip>
                  </Link>
                </div>

                <div className="row mt-2">
                  <div className="col-sm-6">
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Search by patient, doctor, or IPD No..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>
                  <div className="col-sm-6 text-end">
                    <ExportCSV data={data} />
                  </div>
                </div>
              </div>

              <div className="card-body">
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination
                  highlightOnHover
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListRoomChangeEntry;
